import { z } from 'zod';
import { CreateSupplierInvoiceInputSchema, SupplierInvoiceLineItemWriteSchema } from './supplier-invoice.js';
import { ExpenseVatTreatmentSchema } from '../vat.js';
import { LedgerParamsSchema, StrictPeriodReportQuerySchema, YearMonthSchema } from '../ledger.js';
import { depreciationPeriodEndDate } from '../depreciation-period.js';
import { CalendarDateOnlySchema, CalendarDateSchema, LowercaseUuidSchema, PaginationQuerySchema } from './common.js';
// =============================================================================
// Fixed asset register
//
// A persisted register of fixed assets and their posted depreciation
// schedule, for BUSINESS ledgers only. Every posted charge is an ordinary
// ledger entry set under the `asset.depreciate` intent - see
// apps/backend/platform/src/domains/asset for the domain that owns the
// register and the planner. An asset belongs to an asset category, which supplies
// the three default ledger accounts its depreciation charges touch. An asset may
// override those defaults at registration. Chat exposes listing and detail only;
// asset registration and depreciation posting remain MCP-disabled.
// =============================================================================
export const AssetIdParamsSchema = LedgerParamsSchema.extend({
    assetId: z.string().uuid(),
});
/** `includeArchived=true` includes an archived asset in the list and its running totals; the default excludes it (and keeps it out of a depreciation run). */
export const AssetListQuerySchema = PaginationQuerySchema.extend({
    includeArchived: z.enum(['true', 'false']).optional(),
});
export const AssetCategoryIdParamsSchema = LedgerParamsSchema.extend({ assetCategoryId: LowercaseUuidSchema });
export const RenameAssetCategoryInputSchema = z.strictObject({ name: z.string().trim().min(1).max(255) });
export const ArchiveAssetCategoryResponseSchema = z.object({ success: z.boolean() });
export const AssetCategoryListQuerySchema = PaginationQuerySchema;
export const AssetDepreciationPostingsQuerySchema = PaginationQuerySchema;
/** Whether the carried amount is systematically allocated over time. */
export const DepreciationMethodSchema = z.enum(['STRAIGHT_LINE', 'NONE']);
export const BalanceSheetAccountOverrideSchema = z.strictObject({
    costLedgerAccountId: LowercaseUuidSchema,
    accumulatedDepreciationLedgerAccountId: LowercaseUuidSchema.nullable(),
});
export const EffectiveAssetAccountsSchema = z.object({
    costLedgerAccountId: LowercaseUuidSchema,
    accumulatedDepreciationLedgerAccountId: LowercaseUuidSchema.nullable(),
    depreciationChargeLedgerAccountId: LowercaseUuidSchema.nullable(),
    balanceSheetSource: z.enum(['CATEGORY', 'ASSET']),
    depreciationChargeSource: z.enum(['CATEGORY', 'ASSET']),
});
/**
 * User-facing name per depreciation method, shared by the Bill UI and the
 * fixed-asset register exports so the two never drift. Exhaustive over
 * `DepreciationMethod`: adding a member fails to compile until it has a label.
 */
export const DEPRECIATION_METHOD_LABELS = {
    STRAIGHT_LINE: 'Straight line',
    NONE: 'Held at cost',
};
/**
 * `YYYY-MM` calendar period, e.g. `2026-04`. Reuses the shared `YearMonthSchema`
 * (`../ledger.js`) rather than re-spelling the regex: it carries the
 * `(?!00\d{2}-)` guard against `Date.UTC`'s two-digit-year legacy remap, which
 * `asset.helpers.ts`'s `Date.UTC(year, month, 0)` arithmetic depends on.
 */
export const DepreciationPeriodSchema = YearMonthSchema;
/** A real calendar month's final day, supplied by depreciation run callers. */
const DepreciationMonthEndSchema = CalendarDateOnlySchema.refine((value) => {
    if (!DepreciationPeriodSchema.safeParse(value.slice(0, 7)).success)
        return false;
    return value === depreciationPeriodEndDate(value.slice(0, 7));
}, { message: 'Expected the last day of a calendar month' });
export const DepreciationSkipReasonSchema = z.enum([
    'NOT_DEPRECIABLE',
    'NOT_YET_AVAILABLE',
    'UP_TO_DATE',
    'FULLY_DEPRECIATED',
    'DISPOSAL_PENDING',
    'DISPOSED',
]);
/**
 * Whether a claimed disposal's derecognition journal has posted yet. Shared
 * by `AssetSchema.disposalStatus` and `AssetDisposalStateSchema.status` (and
 * the backend's own `AssetDisposalStatus` type alias), so the two-value set
 * is defined once rather than three times.
 */
export const AssetDisposalStatusSchema = z.enum(['PENDING', 'COMPLETED']);
/**
 * Request body for POST /accounts/:accountId/ledgers/:ledgerId/asset-categories.
 *
 * A category supplies the three default ledger accounts for its assets. The
 * defaults are validated here; deliberate asset overrides are validated at
 * registration. `costLedgerAccountId` and
 * `accumulatedDepreciationLedgerAccountId` must be active NON_CURRENT_ASSETS
 * accounts on this ledger and must differ; `depreciationChargeLedgerAccountId`
 * must be an active OPERATING_EXPENSES account. The charge account does not
 * need the `DEPRECIATION` system role - lacking it produces a warning on the
 * response, not a rejection.
 *
 * `defaultUsefulLifeMonths` is required when `defaultDepreciationMethod` is
 * `STRAIGHT_LINE` and must be `null` for `NONE`; the two depreciation accounts
 * follow the same rule. Both defaults are inherited by an asset registered
 * against this category unless that registration states its own.
 */
export const MAX_ASSET_USEFUL_LIFE_MONTHS = 1200;
export const CreateAssetCategoryInputSchema = z.object({
    /** Caller-supplied idempotency key. A retry with the same key returns the same category, creating no second row. */
    idempotencyKey: z.string().min(1).max(255),
    /** Operator-entered label, unique per ledger (e.g. "Motor vehicles - owned"). */
    name: z.string().min(1).max(255),
    costLedgerAccountId: z.string().uuid(),
    accumulatedDepreciationLedgerAccountId: z.string().uuid().nullable(),
    depreciationChargeLedgerAccountId: z.string().uuid().nullable(),
    defaultUsefulLifeMonths: z.number().int().min(1).max(MAX_ASSET_USEFUL_LIFE_MONTHS).nullable(),
    defaultDepreciationMethod: DepreciationMethodSchema,
});
export const AssetCategorySchema = z.object({
    archivedAt: z.string().nullable().optional(),
    id: z.string().uuid(),
    accountId: z.string().uuid(),
    ledgerId: z.string().uuid(),
    name: z.string(),
    costLedgerAccountId: z.string().uuid(),
    accumulatedDepreciationLedgerAccountId: z.string().uuid().nullable(),
    depreciationChargeLedgerAccountId: z.string().uuid().nullable(),
    defaultUsefulLifeMonths: z.number().int().nullable(),
    defaultDepreciationMethod: DepreciationMethodSchema,
    createdAt: z.string(),
    updatedAt: z.string(),
});
/**
 * Response for POST /accounts/:accountId/ledgers/:ledgerId/asset-categories.
 *
 * `warnings` is `[]` when nothing needs flagging. One entry is possible: the
 * depreciation charge account missing the `DEPRECIATION` system role, in which
 * case the cash-flow add-back falls back to matching the account by name -
 * and only finds it when the name contains "depreciation" or "amortisation".
 */
export const CreateAssetCategoryResponseSchema = z.object({
    assetCategory: AssetCategorySchema,
    warnings: z.array(z.string()),
});
export const AssetCategoryListResponseSchema = z.object({
    assetCategories: z.array(AssetCategorySchema),
    nextCursor: z.string().nullable(),
    hasMore: z.boolean(),
});
/**
 * Request body for POST /accounts/:accountId/ledgers/:ledgerId/assets.
 *
 * `assetCategoryId` names the category this asset belongs to. The category supplies the
 * default ledger accounts, depreciation method and useful life. Registration may
 * override the account defaults and may state its own `depreciationMethod` and
 * `usefulLifeMonths` are therefore optional: omit them to inherit the category's
 * defaults, or state them to override. Both are snapshotted onto the asset at
 * registration, so a later change to the category does not rewrite this asset's
 * schedule. An explicit `usefulLifeMonths` is required when the resolved
 * method is `STRAIGHT_LINE` and must be `null` for `NONE`.
 * `openingAccumulatedDepreciationMinorUnits` defaults to 0 when omitted - the
     * depreciation already recognised before this register existed (e.g. a migrated
     * asset). Reconcile it to the general ledger.
 */
export const AssetSourceSupplierInvoiceSchema = z.strictObject({
    supplierInvoiceId: LowercaseUuidSchema,
    lineItemIds: z.array(LowercaseUuidSchema).min(1).max(40).refine((ids) => new Set(ids).size === ids.length, 'Select distinct lines'),
});
export const CreateAssetInputSchema = z.object({
    sourceSupplierInvoice: AssetSourceSupplierInvoiceSchema.optional(),
    /** Caller-supplied idempotency key. A retry with the same key returns the same asset, creating no second row. */
    idempotencyKey: z.string().min(1).max(255),
    /** Short register label. The system assigns the per-ledger asset number. */
    name: z.string().min(1).max(255),
    assetCategoryId: z.string().uuid(),
    balanceSheetAccountOverride: BalanceSheetAccountOverrideSchema.nullable().optional(),
    depreciationChargeLedgerAccountOverrideId: LowercaseUuidSchema.nullable().optional(),
    /** Omit to inherit the category's `defaultDepreciationMethod`. */
    depreciationMethod: DepreciationMethodSchema.optional(),
    /** Omit to inherit the category's `defaultUsefulLifeMonths`; `null` states that the asset has none. */
    usefulLifeMonths: z.number().int().min(1).max(MAX_ASSET_USEFUL_LIFE_MONTHS).nullable().optional(),
    costMinorUnits: z.number().int().min(1),
    /** Original residual estimate. Null preserves that no separate estimate was recorded. */
    residualValueMinorUnits: z.number().int().nonnegative().nullable().optional(),
    /**
     * Depreciation already recognised in the general ledger for this asset
     * before the register starts charging it. Defaults to 0 when omitted.
     * Recorded in the register only: registration posts no journal, so a
     * positive value returns a warning to reconcile it to the ledger.
     */
    openingAccumulatedDepreciationMinorUnits: z.number().int().nonnegative().optional(),
    acquisitionDate: CalendarDateSchema,
    /** Existing acquisition or opening journal in this business ledger; registration posts no journal. */
    acquisitionLedgerEntrySetId: LowercaseUuidSchema.nullable().optional(),
    /**
     * The date the schedule runs from: the asset's available-for-use date, also
     * for an asset migrated with its earlier depreciation in the ledger's opening
     * balances (where the ledger records an opening date, no earlier month is
     * charged). Must not be before `acquisitionDate`.
     */
    depreciationStartDate: CalendarDateSchema,
    notes: z.string().max(2000).nullable().optional(),
});
/** A calculation request has the same operands as registration, but no idempotency key or write. */
export const PreviewAssetRegistrationInputSchema = CreateAssetInputSchema.omit({ idempotencyKey: true });
export const PreviewAssetAccountsInputSchema = z.strictObject({
    assetCategoryId: LowercaseUuidSchema,
    balanceSheetAccountOverride: BalanceSheetAccountOverrideSchema.nullable().optional(),
    depreciationChargeLedgerAccountOverrideId: LowercaseUuidSchema.nullable().optional(),
    depreciationMethod: DepreciationMethodSchema,
    openingAccumulatedDepreciationMinorUnits: z.number().int().nonnegative(),
});
export const PreviewAssetAccountsResponseSchema = z.object({
    balanceSheetAccountOverride: BalanceSheetAccountOverrideSchema.nullable(),
    depreciationChargeLedgerAccountOverrideId: LowercaseUuidSchema.nullable(),
    effectiveAccounts: EffectiveAssetAccountsSchema,
    warnings: z.array(z.string()),
});
export const AssetSchema = z.object({
    sourceSupplierInvoiceId: LowercaseUuidSchema.nullable(),
    sourceLineItemIds: z.array(LowercaseUuidSchema),
    id: z.string().uuid(),
    accountId: z.string().uuid(),
    ledgerId: z.string().uuid(),
    assetNumber: z.string(),
    name: z.string(),
    assetCategoryId: z.string().uuid(),
    /** Resolved through the asset's category, which owns it - not a column on the asset. */
    assetCategoryName: z.string(),
    balanceSheetAccountOverride: BalanceSheetAccountOverrideSchema.nullable(),
    depreciationChargeLedgerAccountOverrideId: LowercaseUuidSchema.nullable(),
    effectiveAccounts: EffectiveAssetAccountsSchema,
    depreciationMethod: DepreciationMethodSchema,
    usefulLifeMonths: z.number().int().nullable(),
    costMinorUnits: z.number().int(),
    residualValueMinorUnits: z.number().int().nullable(),
    residualExceedsCarryingValue: z.boolean(),
    openingAccumulatedDepreciationMinorUnits: z.number().int(),
    currency: z.string().length(3),
    /** ISO 8601 calendar date (YYYY-MM-DD). */
    acquisitionDate: z.string(),
    acquisitionLedgerEntrySetId: z.string().uuid().nullable(),
    /** ISO 8601 calendar date (YYYY-MM-DD). */
    depreciationStartDate: z.string(),
    notes: z.string().nullable(),
    /** Derived: opening accumulated depreciation plus every posted charge. */
    accumulatedDepreciationMinorUnits: z.number().int(),
    /** Derived: cost minus accumulatedDepreciationMinorUnits. */
    carryingValueMinorUnits: z.number().int(),
    /** Cost minus accumulated depreciation, kept after disposal (when carryingValueMinorUnits drops to zero). */
    historicalCarryingValueMinorUnits: z.number().int(),
    /** PENDING while a claimed disposal has not yet posted its derecognition journal. */
    disposalStatus: AssetDisposalStatusSchema.nullable(),
    /** YYYY-MM-DD disposal date, or null when the asset has not been disposed of. */
    disposedOn: z.string().nullable(),
    /** The most recent period with a posted schedule row, or null when none has posted yet. */
    lastPostedPeriod: DepreciationPeriodSchema.nullable(),
    /** ISO 8601 timestamp, or null while the asset is active. A mis-registered asset with no accounting evidence yet can be archived; one that already carries evidence leaves the register only by disposal. */
    archivedAt: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
/**
 * Request body for PATCH /accounts/:accountId/ledgers/:ledgerId/assets/:assetId.
 *
 * Every field is optional; at least one must be supplied. Two tiers apply:
 * `name`, `notes` and `acquisitionLedgerEntrySetId` are descriptive and may
 * change at any time on an un-archived asset. Every other field is an
 * operand - it resolves the asset's depreciation schedule - and can only
 * change while the asset carries no accounting evidence (no posted charge,
 * no positive opening accumulated depreciation, no disposal). `null` on
 * `notes` or `acquisitionLedgerEntrySetId` clears it; omitting a field
 * leaves it unchanged. Operand fields are re-validated together as a merged
 * shape against the same rules `POST .../assets` applies, so e.g. switching
 * `depreciationMethod` to `NONE` must clear `usefulLifeMonths` in the same
 * call.
 */
export const UpdateAssetInputSchema = z
    .object({
    name: z.string().min(1).max(255).optional(),
    notes: z.string().max(2000).nullable().optional(),
    acquisitionLedgerEntrySetId: LowercaseUuidSchema.nullable().optional(),
    assetCategoryId: z.string().uuid().optional(),
    balanceSheetAccountOverride: BalanceSheetAccountOverrideSchema.nullable().optional(),
    depreciationChargeLedgerAccountOverrideId: LowercaseUuidSchema.nullable().optional(),
    costMinorUnits: z.number().int().min(1).optional(),
    residualValueMinorUnits: z.number().int().nonnegative().nullable().optional(),
    usefulLifeMonths: z.number().int().min(1).max(MAX_ASSET_USEFUL_LIFE_MONTHS).nullable().optional(),
    depreciationMethod: DepreciationMethodSchema.optional(),
    acquisitionDate: CalendarDateSchema.optional(),
    depreciationStartDate: CalendarDateSchema.optional(),
    openingAccumulatedDepreciationMinorUnits: z.number().int().nonnegative().optional(),
})
    .refine((data) => Object.keys(data).length > 0, { message: 'At least one field must be supplied' });
/**
 * Response for PATCH .../assets/:assetId.
 *
 * `warnings` mirrors the create response: `[]` when nothing needs flagging,
 * and carries the same opening-accumulated-depreciation / catch-up-charge
 * warnings when an operand change reintroduces one of those conditions.
 */
export const UpdateAssetResponseSchema = z.object({
    asset: AssetSchema,
    warnings: z.array(z.string()),
});
/** Response for DELETE .../assets/:assetId (archive). */
export const ArchiveAssetResponseSchema = z.object({ success: z.boolean() });
/**
 * Response for POST /accounts/:accountId/ledgers/:ledgerId/assets.
 *
 * `warnings` is `[]` when nothing needs flagging. Two entries are possible,
 * and they are mutually exclusive on the opening figure so at most one is
 * ever returned: a positive `openingAccumulatedDepreciationMinorUnits`,
 * which is recorded in the register only - registration posts no journal, so
 * the accumulated depreciation account must already carry that amount in
 * recognised ledger entries for the register to tie to the general ledger; or,
 * when that figure is left at zero, a `depreciationStartDate` before the
 * ledger's opening date, meaning periods before the ledger opened will not
 * be charged; inspect the projected schedule and reconcile earlier charges.
 * The charge account's missing `DEPRECIATION` role is warned about
 * when the category is created, which is where that account is chosen.
 */
export const CreateAssetResponseSchema = z.object({
    asset: AssetSchema,
    warnings: z.array(z.string()),
});
export const AssetListResponseSchema = z.object({
    assets: z.array(AssetSchema),
    nextCursor: z.string().nullable(),
    hasMore: z.boolean(),
});
/** One planned (not-yet-posted) row, as the planner and the preview route produce it. */
export const PlannedDepreciationRowSchema = z.object({
    period: DepreciationPeriodSchema,
    /** ISO 8601 calendar date (YYYY-MM-DD) - the last day of the period. */
    periodEnd: z.string(),
    daysInUse: z.number().int().min(1),
    daysInPeriod: z.number().int().min(1),
    openingCarryingMinorUnits: z.number().int(),
    chargeMinorUnits: z.number().int(),
    accumulatedAfterMinorUnits: z.number().int(),
});
export const PreviewAssetRegistrationResponseSchema = z.object({
    currency: z.string().length(3),
    depreciationMethod: DepreciationMethodSchema,
    usefulLifeMonths: z.number().int().nullable(),
    openingCarryingMinorUnits: z.number().int(),
    totalChargeMinorUnits: z.number().int(),
    firstCharge: PlannedDepreciationRowSchema.nullable(),
    finalCharge: PlannedDepreciationRowSchema.nullable(),
    rows: z.array(PlannedDepreciationRowSchema),
    outcome: z.enum(['SCHEDULE', 'HELD_AT_COST', 'FULLY_DEPRECIATED', 'AT_RESIDUAL']),
    warnings: z.array(z.string()),
});
/**
 * One posted depreciation row. A posting IS a planned row that has actually
 * been journalled, so it carries that row's whole date/amount schedule shape
 * and adds only the identifiers and ledger references posting creates - hence
 * the extend, which keeps the shared seven fields declared once. The route's
 * `mapDepreciationPosting` mirrors this by spreading `mapPlannedRow`.
 */
export const AssetDepreciationPostingSchema = PlannedDepreciationRowSchema.extend({
    id: z.string().uuid(),
    accountId: z.string().uuid(),
    assetId: z.string().uuid(),
    depreciationMethod: DepreciationMethodSchema,
    usefulLifeMonths: z.number().int().min(1),
    currency: z.string().length(3),
    ledgerEntrySetId: z.string().uuid(),
    postedAt: z.string(),
});
/** Response for GET .../assets/:assetId/depreciation-postings. Ascending by period. */
export const AssetDepreciationPostingsResponseSchema = z.object({
    rows: z.array(AssetDepreciationPostingSchema),
    nextCursor: z.string().nullable(),
    hasMore: z.boolean(),
});
/**
 * One financial year of an asset's posted depreciation schedule. The year's
 * bounds come from the ledger's financial year-end month (a calendar year when
 * the ledger has none set). `openingCarryingMinorUnits` is the carrying amount
 * before the year's first posted period, `closingCarryingMinorUnits` the
 * amount after its last, and `rows` the year's posted periods, ascending.
 */
export const AssetDepreciationScheduleYearSchema = z.object({
    /** ISO 8601 calendar date (YYYY-MM-DD) - the first day of the financial year. */
    financialYearStart: z.string(),
    /** ISO 8601 calendar date (YYYY-MM-DD) - the last day of the financial year. */
    financialYearEnd: z.string(),
    openingCarryingMinorUnits: z.number().int(),
    depreciationMinorUnits: z.number().int(),
    closingCarryingMinorUnits: z.number().int(),
    rows: z.array(AssetDepreciationPostingSchema),
});
/**
 * Response for GET .../assets/:assetId/depreciation-schedule: the asset's whole
 * posted schedule grouped by financial year, ascending. Unpaginated because it
 * is bounded by one asset's life - at most one row per month of useful life.
 */
export const AssetDepreciationScheduleResponseSchema = z.object({
    financialYearEndMonth: z.number().int().min(1).max(12),
    years: z.array(AssetDepreciationScheduleYearSchema),
});
/** Mirrors `DepreciationRunResultAssetEntrySchema`'s outcome one-to-one: POST -> POSTED, SKIP -> SKIPPED, FAIL -> FAILED. */
export const DepreciationRunPlanOutcomeSchema = z.enum(['POST', 'SKIP', 'FAIL']);
export const DepreciationRunPlanAssetEntrySchema = z.object({
    assetId: z.string().uuid(),
    assetNumber: z.string(),
    name: z.string(),
    outcome: DepreciationRunPlanOutcomeSchema,
    skipReason: DepreciationSkipReasonSchema.nullable(),
    /** Non-null only on FAIL - the exact message `POST .../assets/depreciation-run` would record for this asset. */
    failureMessage: z.string().nullable(),
    /** Populated only on POST; empty on SKIP and on FAIL. */
    rows: z.array(PlannedDepreciationRowSchema),
});
/**
 * Response for GET .../assets/depreciation-run/preview?throughDate=.
 *
 * The same plan `POST .../assets/depreciation-run` would execute: every
 * asset on the ledger, each `POST` (with the rows still due through
 * `throughPeriod`), `SKIP` (with a reason - not depreciable, not yet
 * available for use, already up to date, fully depreciated, or disposal
 * pending/completed), or `FAIL` (with a `failureMessage` - the asset's
 * category depreciation accounts cannot currently take a posting, e.g. one
 * has been archived since the category was set up). `totalChargeMinorUnits`
 * sums POST rows only. A read only; nothing is written.
 */
export const DepreciationRunPlanSchema = z.object({
    throughDate: DepreciationMonthEndSchema,
    totalChargeMinorUnits: z.number().int().nonnegative(),
    currency: z.string().length(3),
    assets: z.array(DepreciationRunPlanAssetEntrySchema),
});
/**
 * Query for GET .../assets/depreciation-run/preview.
 */
export const DepreciationRunPreviewQuerySchema = z.strictObject({
    throughDate: DepreciationMonthEndSchema,
});
/**
 * Request body for POST /accounts/:accountId/ledgers/:ledgerId/assets/depreciation-run.
 *
 * Posts every asset's due rows through `throughDate` (inclusive) - not
 * just one period, so a register that has fallen behind is caught up in one
 * action. A replay with the same `idempotencyKey` returns the same result
 * without posting again; a fresh call for a period already fully posted
 * returns `postedRowCount: 0` with every asset `SKIPPED` / `UP_TO_DATE`.
 */
export const RunDepreciationInputSchema = DepreciationRunPreviewQuerySchema.extend({
    idempotencyKey: z.string().min(1).max(255),
});
export const DepreciationRunResultAssetEntrySchema = z.object({
    assetId: z.string().uuid(),
    assetNumber: z.string(),
    outcome: z.enum(['POSTED', 'SKIPPED', 'FAILED']),
    skipReason: DepreciationSkipReasonSchema.nullable(),
    postedPeriods: z.array(DepreciationPeriodSchema),
    failureMessage: z.string().nullable(),
});
/**
 * Response for POST .../assets/depreciation-run.
 *
 * Partial failure is reported, not rolled back: one asset's accounts having
 * been archived since registration fails only that asset (`FAILED`, with
 * `failureMessage`) while every other due asset still posts.
 * `durationMs` is the whole run's wall-clock time - a bounded-concurrency
 * per-row-per-asset catch-up is the practical ceiling this prototype
 * measures.
 */
export const DepreciationRunResultSchema = z.object({
    throughDate: DepreciationMonthEndSchema,
    /** Count of individual period ROWS posted across every asset (a catch-up can post several rows for one asset) - not a count of assets, unlike `skippedCount`/`failedCount` below. */
    postedRowCount: z.number().int().nonnegative(),
    skippedCount: z.number().int().nonnegative(),
    failedCount: z.number().int().nonnegative(),
    totalPostedMinorUnits: z.number().int().nonnegative(),
    currency: z.string().length(3),
    durationMs: z.number().int().nonnegative(),
    assets: z.array(DepreciationRunResultAssetEntrySchema),
});
/** Disposal proceeds are net of disposal costs and exclude VAT. They are not a receipt. */
export const AssetDisposalCommandSchema = z.object({
    disposalDate: CalendarDateOnlySchema,
    netProceedsMinorUnits: z.number().int().nonnegative().safe(),
    proceedsLedgerAccountId: LowercaseUuidSchema.nullable(),
    gainLedgerAccountId: LowercaseUuidSchema.nullable(),
    lossLedgerAccountId: LowercaseUuidSchema.nullable(),
});
/** Read-only figures before choosing the account for the calculated gain or loss. */
export const AssetDisposalCalculationRequestSchema = AssetDisposalCommandSchema.pick({
    disposalDate: true,
    netProceedsMinorUnits: true,
});
export const AssetDisposalCalculationResponseSchema = z.object({
    finalChargeMinorUnits: z.number().int(),
    carryingAtDisposalMinorUnits: z.number().int(),
    gainOrLossMinorUnits: z.number().int(),
});
export const AssetDisposalPreviewRequestSchema = AssetDisposalCommandSchema;
export const AssetDisposalConfirmRequestSchema = AssetDisposalCommandSchema.extend({
    previewToken: z.string().length(64),
    idempotencyKey: z.string().min(1).max(255),
});
const AssetDisposalJournalSchema = z.object({
    key: z.string(),
    intent: z.enum(['asset.depreciate', 'asset.dispose']),
    accountingDate: z.string(),
    entries: z.array(z.object({ ledgerAccountId: z.string(), amountMinorUnits: z.number().int(), currency: z.string() })),
});
export const AssetDisposalPreviewResponseSchema = z.object({
    previewToken: z.string(),
    finalChargeMinorUnits: z.number().int(),
    carryingAtDisposalMinorUnits: z.number().int(),
    gainOrLossMinorUnits: z.number().int(),
    journals: z.array(AssetDisposalJournalSchema),
});
export const AssetDisposalStateSchema = z.object({
    assetId: z.string().uuid(),
    status: AssetDisposalStatusSchema,
    disposalDate: z.string(),
    historicalCostMinorUnits: z.number().int(),
    /** Opening plus posted depreciation, including the final charge once it has posted. */
    accumulatedDepreciationMinorUnits: z.number().int(),
    carryingAtDisposalMinorUnits: z.number().int(),
    currentCarryingValueMinorUnits: z.number().int(),
    /** Null until the derecognition journal has posted. */
    netProceedsMinorUnits: z.number().int().nullable(),
    gainOrLossMinorUnits: z.number().int().nullable(),
    ledgerEntrySetId: z.string().uuid().nullable(),
});
export const AssetDisposalReadResponseSchema = z.object({ disposal: AssetDisposalStateSchema.nullable() });
// =============================================================================
// Fixed-asset register report
//
// The IFRS for SMEs s 17.31 shape: per-class cost, accumulated-depreciation and
// carrying-amount roll-forward over a from/as-at window, plus a per-asset
// listing. Depreciation is cut off on `LedgerEntrySet.accountingDate`, the key
// the balance sheet shares, so closing accumulated depreciation agrees with it
// on the as-at date. Cost, and so carrying amount, follows the register's
// acquisition and disposal dates, and the acquisition date is not tied to the
// capitalising journal's date.
// =============================================================================
/** Period-report query: `period` preset XOR both `from` and `to`. */
export const FixedAssetRegisterQuerySchema = StrictPeriodReportQuerySchema;
const MinorUnitsSchema = z.number().int();
/**
 * The roll-forward columns shared by a class and the grand totals. Invariants:
 * `closingCost = openingCost + additions - disposalsCost`,
 * `closingAccumulated = openingAccumulated + charge - released`, and carrying = cost - accumulated.
 */
export const FixedAssetRegisterMovementSchema = z.object({
    openingCostMinorUnits: MinorUnitsSchema,
    additionsMinorUnits: MinorUnitsSchema,
    disposalsCostMinorUnits: MinorUnitsSchema,
    closingCostMinorUnits: MinorUnitsSchema,
    openingAccumulatedDepreciationMinorUnits: MinorUnitsSchema,
    depreciationChargeMinorUnits: MinorUnitsSchema,
    depreciationReleasedOnDisposalMinorUnits: MinorUnitsSchema,
    closingAccumulatedDepreciationMinorUnits: MinorUnitsSchema,
    openingCarryingAmountMinorUnits: MinorUnitsSchema,
    closingCarryingAmountMinorUnits: MinorUnitsSchema,
});
const FixedAssetRegisterRowBaseSchema = z.object({
    assetId: z.string().uuid(),
    assetNumber: z.string(),
    name: z.string(),
    acquisitionDate: CalendarDateOnlySchema,
    depreciationStartDate: CalendarDateOnlySchema,
    depreciationMethod: DepreciationMethodSchema,
    usefulLifeMonths: z.number().int().nullable(),
    costMinorUnits: MinorUnitsSchema,
    /** Equals `costMinorUnits` when `status` is `DISPOSED`, otherwise zero. */
    disposalsCostMinorUnits: MinorUnitsSchema,
    /** `costMinorUnits - disposalsCostMinorUnits`, so a row satisfies closing carrying = closing cost - closing accumulated. */
    closingCostMinorUnits: MinorUnitsSchema,
    openingAccumulatedDepreciationMinorUnits: MinorUnitsSchema,
    depreciationChargeMinorUnits: MinorUnitsSchema,
    /** Zero unless `status` is `DISPOSED`, when it equals opening accumulated depreciation plus the period charge. */
    depreciationReleasedOnDisposalMinorUnits: MinorUnitsSchema,
    closingAccumulatedDepreciationMinorUnits: MinorUnitsSchema,
    closingCarryingAmountMinorUnits: MinorUnitsSchema,
});
/**
 * `DISPOSED` only for a completed disposal dated on or before the as-at date; a
 * pending or later-dated disposal reads `HELD`. `disposedOn` is null exactly
 * when the row is `HELD`, and stays present on the wire either way.
 */
export const FixedAssetRegisterRowSchema = z.discriminatedUnion('status', [
    FixedAssetRegisterRowBaseSchema.extend({ status: z.literal('HELD'), disposedOn: z.null() }),
    FixedAssetRegisterRowBaseSchema.extend({ status: z.literal('DISPOSED'), disposedOn: CalendarDateOnlySchema }),
]);
export const FixedAssetRegisterClassSchema = FixedAssetRegisterMovementSchema.extend({
    assetCategoryId: z.string().uuid(),
    name: z.string(),
    /** Sorted by `assetNumber` ascending. */
    rows: z.array(FixedAssetRegisterRowSchema),
});
export const FixedAssetRegisterSchema = z.object({
    from: CalendarDateOnlySchema,
    asAt: CalendarDateOnlySchema,
    currency: z.string(),
    /** Sorted by name ascending; empty when no asset is held in the window. */
    classes: z.array(FixedAssetRegisterClassSchema),
    totals: FixedAssetRegisterMovementSchema,
});
export const PreviewAssetSourceInputSchema = z.strictObject({
    purchase: CreateSupplierInvoiceInputSchema.pick({ ledgerAccountId: true, lineItems: true }).strict().extend({ currency: z.string().length(3), lineItems: z.array(SupplierInvoiceLineItemWriteSchema.strict()).min(1).max(40) }),
    selectedLinePositions: z.array(z.number().int().nonnegative()).min(1).max(40),
    vatTreatment: ExpenseVatTreatmentSchema.optional(),
    asset: PreviewAssetRegistrationInputSchema.omit({ costMinorUnits: true, acquisitionLedgerEntrySetId: true, sourceSupplierInvoice: true }).strict(),
});
export const PreviewAssetSourceResponseSchema = z.object({
    effectiveCostLedgerAccountId: LowercaseUuidSchema,
    capitalisedCostMinorUnits: z.number().int().min(1), currency: z.string().length(3),
    inputVatClaimed: z.boolean(), registration: PreviewAssetRegistrationResponseSchema,
});
export const AssetBySourceParamsSchema = LedgerParamsSchema.extend({ supplierInvoiceId: LowercaseUuidSchema });
